
import { store } from "../Components/store/store";
import { DashboardApi } from '../apis/DashboardApi';
import { login } from '../reducer/loginSlice';
import configs from '../configs';

class SubscriptionStatusService {
  private static instance: SubscriptionStatusService;
  private intervalId: number | null = null;
  private suspensionCallbacks: Array<() => void> = [];
  private renewalCallbacks: Array<() => void> = [];

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): SubscriptionStatusService {
    if (!SubscriptionStatusService.instance) {
      SubscriptionStatusService.instance = new SubscriptionStatusService();
    }
    return SubscriptionStatusService.instance;
  }

  /**
   * Start monitoring subscription status
   */
  public startMonitoring(checkIntervalMs: number = 900000): this {
    // Clear any existing interval
    this.stopMonitoring();

    // No subscriptions to monitor while payments are off.
    if (!configs.paymentsEnabled) {
      return this;
    }

    // Do an immediate check, but with a small delay to prevent immediate API call
    setTimeout(() => {
      this.checkSubscriptionStatus();
    }, 2000);
    
    // Set up regular checking (every 15 minutes by default)
    this.intervalId = window.setInterval(() => {
      this.checkSubscriptionStatus();
    }, checkIntervalMs);
    
    return this;
  }

  /**
   * Stop monitoring subscription status
   */
  public stopMonitoring(): this {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    return this;
  }
  
  /**
   * Check if monitoring is active
   */
  public isMonitoring(): boolean {
    return this.intervalId !== null;
  }

  /**
   * Register a callback to be called when account is suspended
   */
  public onSuspension(callback: () => void): this {
    this.suspensionCallbacks.push(callback);
    return this;
  }

  /**
   * Register a callback to be called when subscription is renewed
   */
  public onRenewal(callback: () => void): this {
    this.renewalCallbacks.push(callback);
    return this;
  }

  /**
   * Set account as active (call after successful subscription renewal)
   */
  public setAccountActive(): void {
    const currentState = store.getState();
    const userLoginData = currentState.data.login.value;
    
    // Update Redux store to mark account as not suspended
    store.dispatch(login({
      ...userLoginData,
      accountSuspended: false
    }));
    
    // Execute renewal callbacks
    this.renewalCallbacks.forEach(callback => callback());
  }

  // Track the last successful API call time
  private lastApiCallTime: number = 0;
  // Minimum time between API calls (15 seconds)
  private apiRateLimitMs: number = 15000;
  
  /**
   * Check subscription status with rate limiting
   */
  private async checkSubscriptionStatus(): Promise<void> {
    const now = Date.now();
    
    // Check if we're within the rate limit window
    if (now - this.lastApiCallTime < this.apiRateLimitMs) {
      console.log('Subscription check skipped due to rate limiting');
      return;
    }
    
    try {
      const currentState = store.getState();
      const userLoginData:any = currentState.data.login.value;
      
      // Skip check if not authenticated
      if (!userLoginData?.token) return;
      
      // Update last API call time before making the call
      this.lastApiCallTime = now;
      
      const response: any = await DashboardApi.getUserSubscription();
      
      if (response?.data?.status === 'success') {
        const subscription:any = response.data.data.subscription;
        
        // If subscription status is suspended and account isn't already marked as suspended
        if (subscription.status === 'suspended' && userLoginData?.data?.user_plan?.status !== 'suspended') {
          // Update Redux store
          store.dispatch(login({
            ...userLoginData,
            accountSuspended: true
          }));
          
          // Execute suspension callbacks
          this.suspensionCallbacks.forEach(callback => callback());
        }
        
        // If subscription is active but account is marked as suspended (after renewal)
        if (subscription.status === 'active' && userLoginData?.data?.user_plan?.status == 'suspended') {
          this.setAccountActive();
        }
      }
    } catch (error:any) {
      if (error.response && error.response.status === 429) {
        console.warn('Rate limit hit for subscription API. Will retry later.');
        // Increase the rate limit window after hitting a 429
        this.apiRateLimitMs = 60000; // 1 minute
      } else {
        console.error('Error checking subscription status:', error);
      }
    }
  }
}

export default SubscriptionStatusService;